import { ReactNode } from 'react';
import { PillState } from './ReportBlocks';
import { useSheet, useSheetSection } from './Sheet';

export interface DataColumn<Row> {
  key: string;
  title: ReactNode;
  /** a heading spanning several columns, e.g. "Minimum temp" over "Max (A)" and "% limit" */
  group?: string;
  render: (row: Row, index: number) => ReactNode;
  /** colours the cell text pass/warn/fail */
  state?: (row: Row) => PillState | undefined;
  align?: 'left' | 'right';
  mono?: boolean;
}

interface DataTableProps<Row> {
  title?: ReactNode;
  columns: DataColumn<Row>[];
  rows: Row[];
  rowKey?: (row: Row, index: number) => string;
  /** tints the whole row */
  rowState?: (row: Row) => 'fail' | 'warn' | undefined;
  rowId?: (row: Row, index: number) => string | undefined;
  wrap?: boolean;
  emptyText?: string;
  /** print the title as "Table N. …", numbered by the sheet in print order and listed in a TableIndex */
  numbered?: boolean;
  /** stable across renders, for the numbering; the title when it is text */
  tableKey?: string;
}

/** The report's data table: full grid, caption on top, grouped header row when any column declares a group. */
export function DataTable<Row>({ title, columns, rows, rowKey, rowState, rowId, wrap, emptyText = '—', numbered, tableKey }: DataTableProps<Row>) {
  const sheet = useSheet();
  const section = useSheetSection();
  const entry = numbered && title && sheet ? sheet.table(tableKey ?? (typeof title === 'string' ? `${section?.key ?? ''}:${title}` : String(sheet.tables.length)), { title, section: section?.title ?? null }) : null;
  const grouped = columns.some((c) => c.group);
  const groups: Array<{ group: string | undefined; span: number }> = [];
  for (const c of columns) {
    const last = groups[groups.length - 1];
    if (last && last.group === c.group && c.group !== undefined) last.span += 1;
    else groups.push({ group: c.group, span: 1 });
  }
  // a report's own code hands these rows over; an input it reads that the sheet doesn't hold comes as undefined
  const list: Row[] = Array.isArray(rows) ? rows : [];
  const cellClass = (c: DataColumn<Row>, row: Row) => [c.align === 'right' ? 'num' : '', c.mono ? 'mono' : '', c.state?.(row) ? `cell-${c.state(row)}` : ''].filter(Boolean).join(' ') || undefined;

  return (
    <div className="table-scroll">
      <table className={`data ${wrap ? 'wrap' : ''}`} id={entry?.anchor}>
        {title && <caption>{entry ? <><b>Table {entry.number}.</b> {title}</> : title}</caption>}
        <thead>
          {grouped && (
            <tr>
              {groups.map((g, i) => (
                <th key={i} colSpan={g.span} rowSpan={g.group === undefined ? 2 : 1} className={g.group !== undefined ? 'group' : undefined}>
                  {g.group ?? columns[groups.slice(0, i).reduce((n, x) => n + x.span, 0)].title}
                </th>
              ))}
            </tr>
          )}
          <tr>
            {columns.filter((c) => !grouped || c.group !== undefined).map((c) => (
              <th key={c.key} className={c.align === 'right' ? 'num' : undefined}>{c.title}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {list.length === 0 && (
            <tr><td colSpan={columns.length} className="table-empty">{emptyText}</td></tr>
          )}
          {list.map((row, i) => (
            <tr key={rowKey ? rowKey(row, i) : i} id={rowId?.(row, i)} className={rowState?.(row) ? `row-${rowState(row)}` : undefined}>
              {columns.map((c) => (
                <td key={c.key} className={cellClass(c, row)}>{c.render(row, i)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
